import { del as idbDel } from 'idb-keyval';

import type { ApiSessionData } from '../api/types';
import type { DcId, SharedSessionData } from '../types';

import {
  DC_IDS,
  DEBUG, IS_SCREEN_LOCKED_CACHE_KEY,
  REST_SESSION_IDB_KEY_PREFIX,
  SESSION_ACCOUNT_PREFIX,
  SESSION_LEGACY_USER_KEY,
} from '../config';
import { omit } from './iteratees';
import {
  ACCOUNT_SLOT,
  loadSlotSession as loadSlotAccountEntry,
  storeAccountData,
  writeSlotSession,
} from './multiaccount';

// Everything in a slot entry that belongs to the session rather than to the account behind it. The
// rest — name, phone, avatar — is what the account switcher lists the slot by.
const SESSION_ONLY_SLOT_KEYS = [
  'date', 'dcId', 'isTest',
  ...DC_IDS.map((dcId) => `dc${dcId}_auth_key` as const),
  ...DC_IDS.map((dcId) => `dc${dcId}_server_salt` as const),
] satisfies (keyof SharedSessionData)[];

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (slotData) return Boolean(slotData.dcId);

  if (!ACCOUNT_SLOT) {
    const legacyAuthJson = localStorage.getItem(SESSION_LEGACY_USER_KEY);
    if (legacyAuthJson) {
      try {
        const userAuth = JSON.parse(legacyAuthJson);
        return Boolean(userAuth && userAuth.id && userAuth.dcID);
      } catch (err) {
        // Do nothing.
        return false;
      }
    }
  }

  return false;
}

export function storeSession(sessionData: ApiSessionData) {
  const {
    mainDcId, keys, isTest,
  } = sessionData;

  // This fork authenticates over REST, so nothing ever produces MTProto auth keys: the sessions the
  // passcode blob and the multitab-role handover round-trip carry a `userId` and nothing else. Such a
  // session has no slot entry to write (`loadSlotSession` rejects one without `dcId`), and reading
  // `keys` off it threw, which aborted `unlockScreen` before it could clear the lock screen.
  if (!mainDcId || !keys) return;

  const currentSlotData = loadSlotSession(ACCOUNT_SLOT);
  const newSlotData: SharedSessionData = {
    ...currentSlotData,
    dcId: mainDcId,
    isTest,
  };

  Object.keys(keys).map(Number).forEach((dcId) => {
    newSlotData[`dc${dcId as DcId}_auth_key`] = keys[dcId];
  });

  if (!ACCOUNT_SLOT) {
    storeLegacySession(sessionData, currentSlotData?.userId);
  }

  writeSlotSession(ACCOUNT_SLOT, newSlotData);
}

function storeLegacySession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, isTest,
  } = sessionData;

  localStorage.setItem(SESSION_LEGACY_USER_KEY, JSON.stringify({
    dcID: mainDcId,
    id: currentUserId,
    test: isTest,
  }));
  localStorage.setItem('dc', String(mainDcId));
  Object.keys(keys).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
  });
}

export function clearStoredSession(slot?: number) {
  if (!slot) {
    clearStoredLegacySession();
  }

  localStorage.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
}

// Lock paths use this instead of `clearStoredSession`: locking must drop the session, but the slot
// entry is also the account switcher's only record that this slot exists. Removing it made a
// passcode-protected account disappear from every other account's switcher the moment it locked —
// including on the way out of its own tab — and `/` then redirects to whichever slot is active, so
// there was no way back to it.
export function clearStoredSessionKeepAccountInfo(slot?: number) {
  if (!slot) {
    clearStoredLegacySession();
  }

  const slotData = loadSlotAccountEntry(slot);
  if (!slotData) return;

  const accountInfo = omit(slotData, SESSION_ONLY_SLOT_KEYS);
  if (!accountInfo.userId) {
    localStorage.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
    return;
  }

  writeSlotSession(slot, accountInfo);
}

// Drop the REST bearer-token session from the main thread. Sign-out normally lets the API worker do
// this through `destroy`, but a tab that booted straight into the lock screen never runs `initApi`,
// and the connector answers `destroy` with `undefined` while uninitialized — so the token survived,
// and the `initApi` that sign-out triggers next restored it and put the user back in the app.
export function clearPersistedRestSession(slot?: number) {
  return idbDel(`${REST_SESSION_IDB_KEY_PREFIX}${slot || ACCOUNT_SLOT || 1}`).catch(() => undefined);
}

function clearStoredLegacySession() {
  [
    SESSION_LEGACY_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);

  if (!slotData) {
    if (ACCOUNT_SLOT) return undefined;
    return loadStoredLegacySession();
  }

  const sessionData: ApiSessionData = {
    mainDcId: slotData.dcId!,
    keys: DC_IDS.reduce((acc, dcId) => {
      const key = slotData[`dc${dcId}_auth_key` as const];
      if (key) {
        acc[dcId] = key;
      }
      return acc;
    }, {} as Record<number, string>),
    isTest: slotData.isTest || undefined,
  };

  return sessionData;
}

function loadStoredLegacySession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const userAuth = JSON.parse(localStorage.getItem(SESSION_LEGACY_USER_KEY) || 'null');
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored session', err);
      }
      // Do nothing.
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    isTest,
  };
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  try {
    const data = JSON.parse(localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`) || '{}') as SharedSessionData;
    if (!data.dcId) return undefined;
    return data;
  } catch (e) {
    return undefined;
  }
}

export function updateSessionUserId(currentUserId: string) {
  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (!slotData) return;
  storeAccountData(ACCOUNT_SLOT, { userId: currentUserId });
}

export function importTestSession() {
  const sessionJson = process.env.TEST_SESSION!;
  try {
    const sessionData = JSON.parse(sessionJson) as ApiSessionData & { userId: string };
    storeLegacySession(sessionData, sessionData.userId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load test session', err);
    }
  }
}

// The lock flag doubles as the signal other accounts' tabs listen on, so both edges are written from
// the lock and unlock paths themselves. Leaving it to the cache layer missed tabs that are not the
// master one — those stop writing caches entirely — and `localStorage` only notifies on a change, so
// an unlock that left the flag set would swallow the next lock.
export function markScreenLocked() {
  localStorage.setItem(IS_SCREEN_LOCKED_CACHE_KEY, 'true');
}

export function markScreenUnlocked() {
  localStorage.removeItem(IS_SCREEN_LOCKED_CACHE_KEY);
}

export function checkSessionLocked() {
  return localStorage.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === 'true';
}
